/**
 * icons.tsx — the staff-shell OUTLINE icon set (UX Wave 7B, 2026-07-21).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 7B (shell polish
 * to analytics-panel parity).
 *
 * WHY: the CTO's Wave-7B pass replaces the emoji rail glyphs with the
 * analytics webapp's icon language — hand-rolled inline SVGs (no icon-lib
 * dependency), OUTLINE-ONLY strokes in `currentColor` so every icon inherits
 * the rail's text colour in both the rest and active states. All icons are
 * decorative (`aria-hidden`) — the rail item LABEL always carries the
 * accessible name (the Wave-5 rule stands).
 */
import type { ReactNode } from 'react';

/**
 * The drawn size, for the few icons drawn larger or smaller than the rail's
 * 20px (the journey entry loader card, 2026-09-25). The 24-grid, the stroke
 * and every other attribute of the frame are unchanged; omitted, it is 20.
 */
export interface OutlineIconProps {
  size?: number;
}

/** The shared outline-icon frame — 24-grid, single-colour stroke, no fill. */
function OutlineIcon({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={String(size)}
      height={String(size)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Pipeline — a kanban board of three lanes. */
export function IconPipeline() {
  return (
    <OutlineIcon>
      <rect x="3" y="4" width="5" height="16" rx="1.5" />
      <rect x="9.5" y="4" width="5" height="10" rx="1.5" />
      <rect x="16" y="4" width="5" height="13" rx="1.5" />
    </OutlineIcon>
  );
}

/** Patients — the PMS directory (an address book with a contact). */
export function IconPatients() {
  return (
    <OutlineIcon>
      <rect x="4.5" y="3" width="15" height="18" rx="2" />
      <circle cx="12" cy="10" r="2.5" />
      <path d="M8.5 17c.8-1.9 2-2.8 3.5-2.8s2.7.9 3.5 2.8" />
    </OutlineIcon>
  );
}

/** Plan management — a capsule with its half-split seam. */
export function IconPlans() {
  return (
    <OutlineIcon>
      <rect x="4.2" y="9" width="15.6" height="6" rx="3" transform="rotate(-45 12 12)" />
      <path d="M9.5 14.5l5-5" />
    </OutlineIcon>
  );
}

/** Engagement content — an open guide (the PDF library the unlock links serve). */
export function IconEngagementContent() {
  return (
    <OutlineIcon>
      <path d="M12 6c-1.6-1.4-3.5-2-6-2H4v15h2c2.5 0 4.4.6 6 2 1.6-1.4 3.5-2 6-2h2V4h-2c-2.5 0-4.4.6-6 2z" />
      <path d="M12 6v15" />
    </OutlineIcon>
  );
}

/** Users — the staff directory (ported from the analytics rail's icon). */
export function IconUsers() {
  return (
    <OutlineIcon>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </OutlineIcon>
  );
}

/** Payments — a payment card (also the entry loader card's "No charge until approved" step). */
export function IconPayments({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19" />
      <path d="M6 15h4" />
    </OutlineIcon>
  );
}

/** Insights — axis + bars (ported from the analytics rail's Usage icon). */
export function IconInsights() {
  return (
    <OutlineIcon>
      <path d="M3 3v18h18" />
      <rect x="7" y="11" width="3" height="6" rx="0.5" />
      <rect x="12" y="7" width="3" height="10" rx="0.5" />
      <rect x="17" y="13" width="3" height="4" rx="0.5" />
    </OutlineIcon>
  );
}

/** Audit log — the clipboard tracer (ported from the analytics rail's icon). */
export function IconAudit() {
  return (
    <OutlineIcon>
      <path d="M9 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <path d="M8 11h8" />
      <path d="M8 15h5" />
    </OutlineIcon>
  );
}

/** Notify requests — a small bell for upcoming-service expressions of interest. */
export function IconNotify() {
  return (
    <OutlineIcon>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </OutlineIcon>
  );
}

/** My stats — a rising line over the axis. */
export function IconStats() {
  return (
    <OutlineIcon>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 3 3 5-6" />
    </OutlineIcon>
  );
}

/** Your profile — a single person. */
export function IconProfile() {
  return (
    <OutlineIcon>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 21v-.5a6 6 0 0 1 6-6h3a6 6 0 0 1 6 6v.5" />
    </OutlineIcon>
  );
}

/** My activity — a history clock (the clinician's own audit trail, UX Wave 8 §4). */
export function IconActivity() {
  return (
    <OutlineIcon>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </OutlineIcon>
  );
}

/** Dispensing queue — a dispensary mortar and pestle (pharmacy console rail). */
export function IconDispensing() {
  return (
    <OutlineIcon>
      <path d="M4.5 9.5h15v1.5a7.5 7.5 0 0 1-15 0z" />
      <path d="M9.5 20.5h5" />
      <path d="M12 18v2.5" />
      <path d="M14 9.5l4.5-4.5" />
    </OutlineIcon>
  );
}

/** Safety review — a shield with a tick (the clinician sign-off surface). */
export function IconSafety({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <path d="M12 3l7 2.8v5.4c0 4.2-2.9 7.7-7 8.8-4.1-1.1-7-4.6-7-8.8V5.8z" />
      <path d="M9 11.8l2.1 2.2 4-4.2" />
    </OutlineIcon>
  );
}

/** Security — a padlock (the patient password card). */
export function IconLock({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </OutlineIcon>
  );
}

/** « collapse — double chevron (ported from the analytics rail's control). */
export function IconCollapse() {
  return (
    <OutlineIcon>
      <path d="M17 6l-6 6 6 6" />
      <path d="M10 6l-6 6 6 6" />
    </OutlineIcon>
  );
}

/** » expand — double chevron (ported from the analytics rail's control). */
export function IconExpand() {
  return (
    <OutlineIcon>
      <path d="M7 6l6 6-6 6" />
      <path d="M14 6l6 6-6 6" />
    </OutlineIcon>
  );
}

/* ── THE DEAL-DRAWER ACTION ICONS (2026-09-05) ───────────────────────────────
 *
 * The first icons in this file that are NOT rail items. They sit BESIDE the
 * text on the four drawer buttons, and the rules are the file's existing ones,
 * unchanged: the same `OutlineIcon` frame (24-grid, 20×20, `currentColor`
 * stroke 1.8, round caps/joins), `aria-hidden`, `focusable="false"`.
 *
 * ⚠️ DECORATIVE, NEVER THE LABEL. The button's own words carry the accessible
 * name — there is no icon-only variant of any of these, and adding one would
 * break the Wave-5 rule the whole file is built on. An icon that replaces text
 * here would be worse than the emoji glyphs Wave 7B removed, because these four
 * are ACTIONS with consequences rather than navigation.
 *
 * ⚠️ THEY MUST READ AS FOUR DIFFERENT SHAPES AT 20px, and two of them make that
 * load-bearing rather than decorative: **Send plan** and **Request photos** sit
 * next to each other and one of them EMAILS A PATIENT. If a hurried clinician
 * can confuse the two silhouettes, the icon has made the button more dangerous
 * than no icon at all. So they are deliberately built from different primitives
 * rather than from one "arrow" motif — a solid diagonal dart that fills the
 * frame, against an upright box with a lens and a small arrow leaving its
 * corner. Same reasoning separates the tray (a U, opening upward) from the
 * card pair (two offset rectangles).
 */

/** Send plan — a paper plane (one diagonal dart, with its fold). */
export function IconSendPlan() {
  return (
    <OutlineIcon>
      {/* The dart: nose top-right, tail bottom-left, notch at the base. */}
      <path d="M21.5 2.5 2.5 10l7 3.1 3.1 7z" />
      {/* The fold — what makes it a folded sheet rather than a filled arrow. */}
      <path d="M21.5 2.5 9.5 13.1" />
    </OutlineIcon>
  );
}

/**
 * Copy plan link — two offset rounded rectangles (the copy pair).
 *
 * It is THE copy glyph, so the pharmacy sign-in link's compact Copy button
 * (2026-09-25) draws it too, at 16px through the optional `size`; omitted, it
 * is the drawer's 20px, unchanged. One copy shape, never a second one.
 */
export function IconCopyPlanLink({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      {/* The FRONT card, drawn whole. */}
      <rect x="9" y="9" width="11.5" height="11.5" rx="2.5" />
      {/* The BACK card, drawn only where the front one does not cover it — a
          full second rect here would read as a cross-hatch at 20px. */}
      <path d="M6 15h-.5A2.5 2.5 0 0 1 3 12.5v-7A2.5 2.5 0 0 1 5.5 3h7A2.5 2.5 0 0 1 15 5.5V6" />
    </OutlineIcon>
  );
}

/** Upload photos — a tray, with an arrow rising out of it. */
export function IconUploadPhotos() {
  return (
    <OutlineIcon>
      {/* The tray: a U, open at the top, so the arrow reads as leaving it. */}
      <path d="M3 14.5v4A2.5 2.5 0 0 0 5.5 21h13a2.5 2.5 0 0 0 2.5-2.5v-4" />
      {/* The arrow, pointing UP — into the app, which is what an upload is. */}
      <path d="M12 16V3" />
      <path d="M7.5 7.5 12 3l4.5 4.5" />
    </OutlineIcon>
  );
}

/** Request photos — a camera, with an arrow leaving its corner. */
export function IconRequestPhotos() {
  return (
    <OutlineIcon>
      {/* The body is held to x≤15.5 so the outbound arrow has its own corner
          and the two never touch at 20px. */}
      <rect x="2" y="8" width="13.5" height="11" rx="2.5" />
      {/* The viewfinder hump — what makes the box a camera and not a card. */}
      <path d="M6 8l1.4-2.3h4.2L13 8" />
      <circle cx="8.75" cy="13.5" r="3" />
      {/* OUTBOUND: away from the camera, up and to the right. The ask leaves
          us and goes to the patient — it is not another upload. */}
      <path d="M17.5 9.5 22 5" />
      <path d="M18.4 5h3.6v3.6" />
    </OutlineIcon>
  );
}

/**
 * Create another — one circular arrow, turning clockwise (a fresh one of the
 * same thing). The pharmacy sign-in link panel's compact "Create another"
 * button (2026-09-25), beside the copy pair above.
 *
 * ⚠️ DECORATIVE, like every icon here: the button's words ("Create another",
 * plus the visually hidden "pharmacy sign-in link") carry the name. The arc
 * leaves a gap at the top right and the arrowhead sits in it, so at 16px it
 * reads as a loop being closed, never as the copy pair's two rectangles.
 */
export function IconRefresh({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      {/* 315° of an r=8 circle round (12,12), clockwise from 3 o'clock to
          half past one, leaving the top-right gap open. */}
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      {/* The arrowhead: an L whose corner meets the end of the arc. */}
      <path d="M18.2 2.6v4.1h-4.1" />
    </OutlineIcon>
  );
}

/* ── THE JOURNEY ENTRY LOADER CARD'S ICONS (2026-09-25) ──────────────────────
 *
 * The CTO, on the entry loader's "How it works" card: "right now it's too much
 * text. Let's try to use more icons … grab the attention because we have a very
 * short window." So each step and each chip of `ui/LoaderJourneyCard.tsx` leads
 * with one of these, drawn at 24px (steps) or 18px (chips) inside a soft tinted
 * disc. The house rules, unchanged: the `OutlineIcon` frame (24-grid,
 * `currentColor` stroke 1.8, round caps and joins, no fill), `aria-hidden`,
 * `focusable="false"`.
 *
 * ⚠️ DECORATIVE, NEVER THE LABEL. Each sits beside a short headline that
 * carries the meaning; the card reads the same to a screen reader with every
 * icon removed. ⚠️ AND PUBLIC-SAFE: the card shows before any eligibility
 * decision, so no glyph may suggest a medicine or a delivery form (no pen, no
 * syringe, no pill) — a stopwatch, a card (`IconPayments`), a stethoscope, a
 * parcel, a phone, a price tag and a speech bubble. The last three moved here
 * from the card's own inline set, so the portal has one icon set. The CHECKOUT
 * loader's card (same component, 2026-09-25) adds one: a checklist
 * (`IconChecklist`, at the end of this file) for "Choose your treatment & plan".
 */

/** A stopwatch — how long the check takes. */
export function IconStopwatch({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <circle cx="12" cy="13.5" r="7.5" />
      <path d="M10 2.5h4" />
      <path d="M12 2.5V6" />
      <path d="M12 13.5V9.8" />
      <path d="M18.2 7.3l1.3-1.3" />
    </OutlineIcon>
  );
}

/** A stethoscope — the clinician's review. */
export function IconStethoscope({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      {/* The earpieces and the binaural U. */}
      <path d="M5 3.5h2" />
      <path d="M15 3.5h2" />
      <path d="M6 3.5V8a5 5 0 0 0 10 0V3.5" />
      {/* The tube down and round to the chest piece. */}
      <path d="M11 13v1.5a4 4 0 0 0 8 0v-1.8" />
      <circle cx="19" cy="10.5" r="2.2" />
    </OutlineIcon>
  );
}

/** A parcel — what is sent out once a clinician approves. */
export function IconParcel({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <path d="M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4Z" />
      <path d="M3.5 7.5 12 11.5l8.5-4" />
      <path d="M12 11.5v9" />
      <path d="M7.8 5.5l8.4 4" />
    </OutlineIcon>
  );
}

/** A phone handset — a call back. */
export function IconPhone({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <path d="M5 4h3.2l1.6 4-2 1.3a10.5 10.5 0 0 0 4.9 4.9l1.3-2 4 1.6V17a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z" />
    </OutlineIcon>
  );
}

/** A price tag — Switch & Save. */
export function IconPriceTag({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.4 1.4 0 0 1 0 2l-6.3 6.3a1.4 1.4 0 0 1-2 0Z" />
      <circle cx="8.2" cy="8.2" r="1.4" />
    </OutlineIcon>
  );
}

/** A speech bubble — our team. */
export function IconChat({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-9l-4 3.5V17H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
    </OutlineIcon>
  );
}

/**
 * A clipboard with two ticked rows — choosing the treatment and the plan: the
 * first step of the CHECKOUT loader's card (`checkout/checkoutLoaderContent.ts`,
 * 2026-09-25). A list being ticked off, never a medicine: the capsule of
 * `IconPlans` is a staff-console glyph and stays off every patient card (R28 —
 * no pill, pen or syringe shape on a journey surface).
 */
export function IconChecklist({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <rect x="4.5" y="4" width="15" height="17" rx="2.2" />
      <path d="M9 4V2.8h6V4" />
      <path d="M8 10.3l1.6 1.6 2.8-2.9" />
      <path d="M14.5 10.6h2" />
      <path d="M8 15.8l1.6 1.6 2.8-2.9" />
      <path d="M14.5 16.1h2" />
    </OutlineIcon>
  );
}

/* ── THE CHECKOUT FAMILY'S TWO GLYPHS (2026-09-26, CTO: "in the other one you
 * use icons and then chips … align to the same in both screens") ────────────
 *
 * The plan screen's "What you're agreeing to" became the same icon-led list
 * (`ui/IconPoints.tsx`) the card step's hold box already was, and the q15
 * agreement step gained a phone-only jump to its tick. Two shapes were missing
 * from the set; both sit on the house `OutlineIcon` frame and both are
 * decorative — the words beside them (or the button's accessible name) carry
 * every meaning.
 */

/**
 * A calendar page — the commitment: how many months, at what price, billed
 * monthly. The plan screen's first agreement point (`checkout/CommitmentTerms`).
 */
export function IconCalendar({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.2" />
      <path d="M3.5 10h17" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M8 14h2" />
      <path d="M14 14h2" />
    </OutlineIcon>
  );
}

/**
 * An arrow pointing down — "go further down the page". The q15 agreement
 * step's floating jump to the agreement tick on a phone
 * (`journey/QuestionCard.tsx`, `AgreeJumpButton`). The button's accessible name
 * ("Go to the agreement tick") is the label; this is the drawing only.
 */
export function IconArrowDown({ size }: OutlineIconProps = {}) {
  return (
    <OutlineIcon size={size}>
      <path d="M12 4.5v14" />
      <path d="M6 12.5l6 6 6-6" />
    </OutlineIcon>
  );
}
